import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LuLayoutDashboard, LuServer, LuCloud, LuChartPie, LuBell, LuLogOut, LuActivity, LuMenu, LuX, LuShield, LuUsers, LuClipboardCheck, LuKeyRound } from 'react-icons/lu';
import GlobalSearch from './GlobalSearch';
import ScopeSwitcher from './ScopeSwitcher';

const Navbar = ({ user, onLogout }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: LuLayoutDashboard, label: 'Dashboard' },
    { path: '/resources', icon: LuServer, label: 'Resources' },
    { path: '/providers', icon: LuCloud, label: 'Providers' },
    { path: '/analytics', icon: LuChartPie, label: 'Analytics' },
    { path: '/alerts', icon: LuBell, label: 'Alerts' },
    { path: '/work', icon: LuClipboardCheck, label: 'My Work' },
    { path: '/settings/access', icon: LuUsers, label: 'People' },
  ];

  return (
    <nav className="bg-zinc-900/80 backdrop-blur-xl border-b border-zinc-800/50 sticky top-0 z-50">
      <div className="mx-auto max-w-[96rem] px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 md:gap-8">
            {/* Logo - Minimal, confident */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                <LuActivity className="w-5 h-5 text-zinc-900" />
              </div>
              <span className="text-lg font-semibold tracking-tight text-zinc-100">Cloud Watcher</span>
            </Link>
            
            {/* Desktop Navigation - Understated */}
            <div className="hidden 2xl:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                return (
                  <Link key={item.path} to={item.path} aria-current={isActive ? 'page' : undefined} data-testid={`nav-${item.label.toLowerCase()}`}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 ${
                        isActive
                          ? 'bg-zinc-800 text-amber-400'
                          : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="hidden xl:block">
            <ScopeSwitcher />
          </div>
          
          {/* Global Search */}
          <GlobalSearch />

          {/* User section - Minimal */}
          <div className="flex items-center gap-2 md:gap-3">
            <div className="xl:hidden">
              <ScopeSwitcher summary />
            </div>
            <div className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-1.5 rounded-lg">
              <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700">
                <span className="text-sm font-medium text-zinc-300">{user?.name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="hidden xl:block">
                <p className="text-sm font-medium text-zinc-200">{user?.name}</p>
                {user?.role === 'admin' && <p className="text-[10px] uppercase tracking-wider text-amber-400">Administrator</p>}
              </div>
            </div>

            <Link
              to="/settings/security"
              className="hidden 2xl:flex p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
              aria-label="Security settings"
            >
              <LuShield className="w-4 h-4" />
            </Link>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onLogout}
              data-testid="logout-button"
              className="hidden 2xl:flex p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
              aria-label="Log out"
            >
              <LuLogOut className="w-4 h-4" />
            </motion.button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="2xl:hidden p-2 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <LuX className="w-6 h-6" /> : <LuMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            id="mobile-navigation"
            className="2xl:hidden border-t border-zinc-800/50 bg-zinc-900 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-2">
              <div className="xl:hidden mb-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Working in</p>
                <ScopeSwitcher />
              </div>
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                return (
                  <Link 
                    key={item.path} 
                    to={item.path} 
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div
                      className={`px-4 py-3 rounded-lg flex items-center gap-3 transition-all ${
                        isActive
                          ? 'bg-zinc-800 text-amber-400'
                          : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-base font-medium">{item.label}</span>
                    </div>
                  </Link>
                );
              })}
              <div className="pt-4 mt-4 border-t border-zinc-800/50">
                <Link to="/settings/security" onClick={() => setIsMenuOpen(false)} className="w-full px-4 py-3 rounded-lg flex items-center gap-3 text-zinc-400 hover:bg-zinc-800 transition-all">
                  <LuShield className="w-5 h-5" />
                  <span className="text-base font-medium">Security</span>
                </Link>
                <Link to="/settings/ssh-identity" onClick={() => setIsMenuOpen(false)} className="w-full px-4 py-3 rounded-lg flex items-center gap-3 text-zinc-400 hover:bg-zinc-800 transition-all">
                  <LuKeyRound className="w-5 h-5" />
                  <span className="text-base font-medium">SSH devices</span>
                </Link>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    onLogout();
                  }}
                  className="w-full px-4 py-3 rounded-lg flex items-center gap-3 text-red-400 hover:bg-red-500/10 transition-all"
                >
                  <LuLogOut className="w-5 h-5" />
                  <span className="text-base font-medium">Logout</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
